import { jwtDecode } from "jwt-decode";

import { FeatureType } from "@/types/FeatureType";

export type PermissionToken = string;
export type PermissionSet = ReadonlySet<PermissionToken>;

const ADMIN_ROLE = "SprengConnect.Admin";
const ADMIN_PERMISSION = "ADMIN";

const normalize = (s: unknown): string => (typeof s === "string" ? s : String(s ?? "")).trim().toUpperCase();

const safeDecode = <T = Record<string, unknown>>(token?: string | null): T | null => {
  if (!token) return null;
  try {
    return jwtDecode<T>(token);
  } catch {
    return null;
  }
};

export const authorizationService = {
  isAdmin: (token?: string | null): boolean => {
    const decoded = safeDecode<Record<string, unknown>>(token);
    if (!decoded) return false;

    // roles/permissions may be arrays of strings or absent
    const rolesRaw = decoded.roles;
    const permissionsRaw = decoded.permissions;

    const roles =
      Array.isArray(rolesRaw) && rolesRaw.every((r) => typeof r === "string")
        ? (rolesRaw as string[]).map(normalize)
        : [];

    const permissions =
      Array.isArray(permissionsRaw) && permissionsRaw.every((p) => typeof p === "string")
        ? (permissionsRaw as string[]).map(normalize)
        : [];

    return roles.includes(normalize(ADMIN_ROLE)) || permissions.includes(normalize(ADMIN_PERMISSION));
  },
};

export const extractRawPermissions = (token?: string | null): string[] => {
  const decoded = safeDecode<Record<string, unknown>>(token);
  if (!decoded) return [];

  const raw = decoded.permissions;
  if (!Array.isArray(raw)) return [];

  return raw.filter((p): p is string => typeof p === "string" && p.trim().length > 0).map((p) => normalize(p));
};

export const createPermissionSet = (tokens: string[] | null | undefined): PermissionSet => {
  if (!tokens?.length) return new Set();
  return new Set(tokens.filter(Boolean).map(normalize));
};

export const hasFeatureForAnyIndex = (permissionSet: PermissionSet, feature: FeatureType): boolean => {
  if (!permissionSet?.size) return false;
  const suffix = `-${normalize(feature)}`;
  for (const tok of permissionSet) {
    if (tok.endsWith(suffix)) return true;
  }
  return false;
};

export const hasFeature = (permissionSet: PermissionSet, module: string, feature: string): boolean => {
  if (!permissionSet?.size) return false;
  const token = `${normalize(module)}-${normalize(feature)}`;
  return permissionSet.has(token);
};
